import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { connect } from '@puzzel/widget-api-lib'
import { type Config, getConfig } from 'components/api/useConfig'
import { API_PREFIX } from 'config'
import ky from 'ky'
import { usePostHog } from 'posthog-js/react'
import { useEffect, useRef, useState, type FC } from 'react'
import { setAmplitudeUserId } from 'utils/amplitude'

import { extractHttpError } from './components/api/extractHttpError'
import ErrorComponent from './components/ErrorComponent'
import { extractLocale } from './components/extractLocale'
import NotConnected from './components/NotConnected'
import { logEvents } from './copilot/logEvents'
import Widget from './Widget'

type SystemCall = {
  system_call_progress:
    | 'CONNECTED'
    | 'HANGUP'
    | 'ALERTING'
    | 'NOANSWER'
    | 'ABORTED'
    | 'SPLIT'
    | 'BUSY'
  system_caller_ano: string
  language: string
  callerStatus: 'New Call'
  current_state: string
  system_caller_transfered: boolean
  __CLIENT_IS_OUTBOUND__: boolean
  ciq_destination: string
  system_session_id: string
}

type PuzzelCall = {
  sessionId: string
  caller: string
}

const PuzzelLayer: FC = () => {
  const { i18n } = useLingui()
  const posthog = usePostHog()
  const [customer, setCustomer] = useState<string | null>(null)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [previousCustomer, setPreviousCustomer] = useState<string>('')
  const [apiToken, setApiToken] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const configRef = useRef<Config | null>(null)

  const defaultWaitForConnectionBeforeShowingSummary =
    configRef.current === null
      ? true
      : configRef.current.waitForConnectionBeforeShowingSummary

  if (
    defaultWaitForConnectionBeforeShowingSummary === true &&
    customer != null &&
    customer !== previousCustomer
  ) {
    setIsConnected(false)
    setPreviousCustomer(customer)
  }

  if (customer == null && isConnected === true) {
    logEvents({ eventType: 'missing customer', level: 'warn' })
  }

  // oxlint-disable-next-line react/exhaustive-deps -- only run on init
  useEffect(() => {
    const queryString = window.location.search
    const params = new URLSearchParams(queryString)
    const organizationUid = params.get('org')

    if (organizationUid) {
      posthog?.identify(organizationUid)
      setAmplitudeUserId(organizationUid)
    }

    let apiToken: string | null = null
    let debugLogging = false
    let waitForConnectionBeforeShowingSummary = true

    const doStuff = async (): Promise<void> => {
      try {
        const api = await connect()
        apiToken = params.get('token')
        if (!apiToken) {
          throw new Error('No token found')
        }
        setApiToken(apiToken)
        const rawLocale = api?._config?.system?.locale
        const locale = extractLocale(rawLocale)

        if (locale) {
          i18n.activate(locale)
        } else {
          logEvents({
            eventType: 'invalid locale',
            level: 'warn',
            raw: rawLocale,
            extracted: extractLocale(locale),
          })
        }

        if (configRef.current == null) {
          const r = await getConfig(apiToken)

          debugLogging = r.debugLogging
          waitForConnectionBeforeShowingSummary =
            r.waitForConnectionBeforeShowingSummary

          configRef.current = r

          if (debugLogging) {
            logEvents({
              eventType: 'initialized',
              level: 'info',
              org: params.get('org'),
              api,
            })
          }
        } else {
          debugLogging = configRef.current.debugLogging
          waitForConnectionBeforeShowingSummary =
            configRef.current.waitForConnectionBeforeShowingSummary
        }

        api.on('SYSTEM_REQUEST_IS_CONNECTED', () => {
          if (debugLogging) {
            logEvents({
              eventType: 'SYSTEM_REQUEST_IS_CONNECTED',
              level: 'info',
            })
          }
          // oxlint-disable-next-line no-console
          console.log('SYSTEM_REQUEST_IS_CONNECTED')
          setIsConnected(true)
        })

        api.on('SYSTEM_INCOMING_CALL', (data: { call: PuzzelCall }) => {
          const { call } = data || {}
          // oxlint-disable-next-line no-console
          console.log('SYSTEM_INCOMING_CALL:', call)
          setCustomer(call?.caller)
          setSessionId(call?.sessionId)

          if (waitForConnectionBeforeShowingSummary === false) {
            setIsConnected(true)
          }

          if (debugLogging) {
            logEvents({
              eventType: 'SYSTEM_INCOMING_CALL',
              level: 'info',
              hasCaller: !!call?.caller,
              waitForConnectionBeforeShowingSummary,
            })
          }
        })

        api.on('SYSTEM_CALL_TRIGGER', (vars: SystemCall) => {
          // oxlint-disable-next-line no-console
          console.log('SYSTEM_CALL_TRIGGER:', vars)
          const {
            system_call_progress,
            __CLIENT_IS_OUTBOUND__,
            ciq_destination,
          } = vars

          if (debugLogging) {
            logEvents({
              eventType: 'SYSTEM_CALL_TRIGGER',
              level: 'info',
              hasCaller: !!ciq_destination,
              waitForConnectionBeforeShowingSummary,
              system_call_progress,
              __CLIENT_IS_OUTBOUND__,
            })
          }

          if (
            waitForConnectionBeforeShowingSummary &&
            system_call_progress === 'CONNECTED'
          ) {
            setIsConnected(true)
          } else if (__CLIENT_IS_OUTBOUND__ === true) {
            setIsConnected(true)
          } else if (
            waitForConnectionBeforeShowingSummary === false &&
            system_call_progress === 'ALERTING'
          ) {
            setIsConnected(true)
          }

          const customer = ciq_destination
          setCustomer(customer)
          if (!customer) {
            logEvents({
              eventType: 'SYSTEM_CALL_TRIGGER customer number empty',
              level: 'warn',
              hasCaller: !!ciq_destination,
              waitForConnectionBeforeShowingSummary,
              system_call_progress,
              __CLIENT_IS_OUTBOUND__,
            })
          }
        })

        api.on(
          'SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER',
          async (data: unknown): Promise<void> => {
            try {
              // oxlint-disable-next-line no-console
              console.log('SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER :', data)
              await ky.post(`${API_PREFIX}/puzzel-widget/live-transcription`, {
                json: {
                  type: 'SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER',
                  data,
                },
                credentials: 'include',
                mode: 'cors',
                headers: {
                  Authorization: apiToken || undefined,
                },
              })
            } catch (error) {
              console.error('error :', error)
              logEvents({
                eventType: 'SYSTEM_CALL_ASR_TRANSCRIPTION_TRIGGER error',
                level: 'warn',
                err: error,
              })
            }
          },
        )
        api.on(
          'SYSTEM_CALL_ASR_TRANSCRIPTION',
          async (data: unknown): Promise<void> => {
            // oxlint-disable-next-line no-console
            console.log('SYSTEM_CALL_ASR_TRANSCRIPTION :', data)
            try {
              await ky.post(`${API_PREFIX}/puzzel-widget/live-transcription`, {
                json: {
                  type: 'SYSTEM_CALL_ASR_TRANSCRIPTION',
                  data,
                },
                credentials: 'include',
                mode: 'cors',
                headers: {
                  Authorization: apiToken || undefined,
                },
              })
            } catch (error) {
              console.error('error :', error)
              logEvents({
                eventType: 'SYSTEM_CALL_ASR_TRANSCRIPTION error',
                level: 'warn',
                err: error,
              })
            }
          },
        )

        await api.ready()
      } catch (error) {
        const e = await extractHttpError(error)
        logEvents({
          eventType: 'init error',
          level: 'error',
          name: e.name,
          message: e.message,
          ...e.cause,
        })

        // The extracted error, not the original: this screen shows the message
        // and the cause, and only the extracted one is redacted.
        setError(e)
      }
    }

    doStuff()
  }, [])

  if (error) return <ErrorComponent error={error} />
  if (apiToken == null) return <div />

  if (!isConnected) return <NotConnected />
  if (!customer)
    return <ErrorComponent error={new Error(t`Missing customer number`)} />

  return (
    <Widget apiToken={apiToken} filter={{ customer }} sessionId={sessionId} />
  )
}

export default PuzzelLayer
